/**
 * Pure, React-vrije helpers voor de Combobox-familie zodat de filter-, groep-
 * en toggle-logica unit-testbaar is zonder jsdom (codebase-conventie).
 */

export interface ComboboxOption {
  /** Stabiele identifier — komt terug in `value`/`onChange`. */
  value: string
  /** Weergavetekst — ook gebruikt om op te filteren. */
  label: string
  /** Optionele subtekst tussen haakjes (bv. functie of BV). Ook doorzoekbaar. */
  hint?: string
  /** Optionele groepskop (bv. BV-naam). Ook doorzoekbaar (FO-482: "band" vindt Wagenpark › Banden). */
  group?: string
  /**
   * batch-65 (FO-482) — onzichtbare extra zoektermen (bv. de zoekwoorden van een
   * kostensoort: "diesel" vindt Brandstof). Niet getoond, wel doorzoekbaar.
   */
  keywords?: ReadonlyArray<string>
  /**
   * Niet-kiesbaar (grijs). Gebruikt voor opties die zichtbaar moeten zijn met
   * uitleg maar niet geselecteerd mogen worden (bv. een geblokkeerde leverancier
   * met de reden in de hint). De Combobox laat de optie zien maar selecteert niet.
   */
  disabled?: boolean
  /**
   * Korte tekst rechts in de rij, bv. een match-score ("3/5"). Bewust NIET
   * doorzoekbaar: het is een sorteer-/leeshulp, geen identificerend gegeven, en
   * je wilt niet dat typen op "3" ineens op scores matcht.
   */
  suffix?: string
  /**
   * Native tooltip op de rij (bv. welke criteria ontbreken). Bewust de
   * browser-tooltip en geen eigen component: hij werkt in een portal-popover
   * zonder extra positionering. Let op dat hij op touch NIET verschijnt - zet
   * er dus alleen detail in dat de gebruiker ook zonder kan (de `suffix` en de
   * omliggende UI moeten het verhaal al vertellen).
   */
  title?: string
}

/**
 * Case-insensitive substring-match op label, hint, groep en (onzichtbare)
 * keywords. Een query met meerdere woorden matcht als ELK woord ergens in die
 * velden voorkomt ("wagenpark band" vindt Banden in de groep Wagenpark). Lege
 * query → alles. Een optie die de hele query als substring bevat matcht altijd
 * (superset van het oude label+hint-gedrag).
 */
export function filterOptions(
  options: ReadonlyArray<ComboboxOption>,
  query: string,
): ComboboxOption[] {
  const q = query.trim().toLowerCase()
  if (!q) return [...options]
  const termen = q.split(/\s+/).filter((t) => t.length > 0)
  return options.filter((o) => {
    const velden = [o.label, o.hint, o.group, ...(o.keywords ?? [])]
      .filter((v): v is string => typeof v === 'string' && v.length > 0)
      .map((v) => v.toLowerCase())
    if (velden.some((v) => v.includes(q))) return true
    return termen.every((t) => velden.some((v) => v.includes(t)))
  })
}

/** Groepeer op `group`, met behoud van eerste-voorkomen-volgorde. */
export function groupOptions(
  options: ReadonlyArray<ComboboxOption>,
): Array<{ group?: string; items: ComboboxOption[] }> {
  const order: Array<string | undefined> = []
  const map = new Map<string | undefined, ComboboxOption[]>()
  for (const o of options) {
    if (!map.has(o.group)) {
      map.set(o.group, [])
      order.push(o.group)
    }
    map.get(o.group)!.push(o)
  }
  return order.map((g) => ({ group: g, items: map.get(g)! }))
}

/** Voeg toe of verwijder een value (multi-select toggle). */
export function toggleValue(
  values: ReadonlyArray<string>,
  value: string,
): string[] {
  return values.includes(value)
    ? values.filter((v) => v !== value)
    : [...values, value]
}
